import { nowIso } from "./dates.js";

// Manual corrections to catalog data (design_documents/overrides-and-relink.md).
//
// Sources are messy: wrong covers, edition-flavoured titles, the odd bad match.
// Two remedies share one mechanism here:
//
//   • EDIT a field in place. The edited value is written INTO the normal field
//     (`title`, `cover_url`, …) so every reader shows it unchanged, and the
//     catalog's own value is parked in `source_values[field]`. A key in
//     `source_values` means "the user overrode this": catalog refreshes update
//     the parked value instead of the field, and "reset" swaps it back.
//   • RE-LINK the item to a different source record: catalog fields are replaced
//     (through the same lock-aware path), the external id swaps, and the previous
//     link is remembered in `relinked_from` so it can be undone.
//
// Everything in this module is pure; db/overrides.js does the reads/writes.

export const OVERRIDABLE_FIELDS = {
	videogame: ["title", "cover_url", "release_year", "release_date"],
	book: ["title", "cover_url", "published_year"],
	tv: ["title", "cover_url", "premiered_year"],
	anime: ["title", "cover_url", "premiered_year"],
	movie: ["title", "cover_url", "release_year", "release_date"],
	comic: ["title", "cover_url", "release_year"],
};

// The field that carries the source's own id, per media type. Never overridable.
export const EXTERNAL_KEY = {
	videogame: "igdb_id",
	book: "ol_id",
	tv: "tvmaze_id",
	anime: "tvmaze_id",
	movie: "imdb_id",
	// "<source>:<external_id>" — comics have two sources (db/comics.js).
	comic: "comic_key",
};

// Sentinel a patch can carry instead of a value: "put this field back to what the
// catalog says".
export const RESET = "__reset_to_catalog__";

export function yearField(mediaType) {
	if (mediaType === "videogame" || mediaType === "movie" || mediaType === "comic") return "release_year";
	if (mediaType === "book") return "published_year";
	return "premiered_year";
}

export function isOverridable(mediaType, field) {
	return (OVERRIDABLE_FIELDS[mediaType] ?? []).includes(field);
}

export function overriddenFields(record) {
	return Object.keys(record?.source_values ?? {});
}

export function isOverridden(record, field) {
	const sv = record?.source_values;
	return sv != null && Object.prototype.hasOwnProperty.call(sv, field);
}

function sameValue(a, b) {
	return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

// Set a field to the user's value. The first override of a field parks the
// current (catalog) value in source_values; later edits leave that parked value
// alone, so "reset" always goes back to the catalog, not to the previous edit.
export function withOverride(record, field, value) {
	const sv = { ...(record.source_values ?? {}) };
	if (!Object.prototype.hasOwnProperty.call(sv, field)) sv[field] = record[field] ?? null;
	return { ...record, [field]: value, source_values: sv };
}

// Put a field back to the catalog's value and forget the override.
export function withoutOverride(record, field) {
	if (!isOverridden(record, field)) return record;
	const sv = { ...record.source_values };
	const original = sv[field];
	delete sv[field];
	return { ...record, [field]: original, source_values: sv };
}

// The record as the catalog sees it — overridden fields replaced by their parked
// source values. Refresh diffs run against this, never against the user's edits.
export function catalogView(record) {
	return { ...record, ...(record?.source_values ?? {}) };
}

// Write catalog data onto a record, respecting overrides: an overridden field
// keeps the user's value and only its source_values entry is updated; every other
// field is written directly. Returns { next, changed }.
export function applyCatalogFields(record, patch) {
	const next = { ...record };
	const sv = { ...(record?.source_values ?? {}) };
	let changed = false;
	for (const [key, value] of Object.entries(patch ?? {})) {
		if (Object.prototype.hasOwnProperty.call(sv, key)) {
			if (!sameValue(sv[key], value)) {
				sv[key] = value ?? null;
				changed = true;
			}
		} else if (!sameValue(next[key], value)) {
			next[key] = value;
			changed = true;
		}
	}
	next.source_values = sv;
	return { next, changed };
}

// Copy the given keys from a fresh API record when present (undefined = the
// endpoint didn't say; leave the field alone). null stays null (or a default).
function pick(fresh, keys, defaults = {}) {
	const out = {};
	for (const k of keys) {
		if (fresh[k] === undefined) continue;
		out[k] = fresh[k] ?? (defaults[k] ?? null);
	}
	return out;
}

// The catalog fields a fresh source record supplies, per media type — the shape
// `applyCatalogFields` / `relinkRecord` consume. `taxonomy` is the MAL enrichment
// for anime (omit it to keep the record's current genres).
export function catalogPatchFrom(mediaType, fresh, { taxonomy } = {}) {
	if (!fresh) return null;
	if (mediaType === "videogame") {
		return {
			igdb_id: fresh.igdb_id,
			title: fresh.name ?? fresh.title ?? null,
			...pick(
				fresh,
				["cover_url", "release_year", "release_date", "platforms", "genres", "summary", "franchise", "collection"],
				{ platforms: [], genres: [] },
			),
		};
	}
	if (mediaType === "book") {
		return {
			ol_id: fresh.ol_id,
			title: fresh.title ?? null,
			...pick(fresh, ["authors", "published_year", "cover_url", "subjects", "summary"], {
				authors: [],
				subjects: [],
			}),
		};
	}
	if (mediaType === "movie") {
		return {
			imdb_id: fresh.imdb_id,
			title: fresh.title ?? null,
			...pick(
				fresh,
				[
					"cover_url", "release_year", "release_date", "summary", "runtime", "rated",
					"genres", "directors", "writers", "cast", "imdb_rating",
				],
				{ genres: [], directors: [], writers: [], cast: [] },
			),
		};
	}
	const show = {
		tvmaze_id: fresh.tvmaze_id,
		title: fresh.title ?? null,
		...pick(fresh, ["premiered_year", "cover_url", "summary"]),
	};
	if (mediaType === "tv") return show;
	if (mediaType === "anime") {
		return {
			...show,
			...(taxonomy
				? {
						mal_id: taxonomy.mal_id ?? null,
						genres: taxonomy.genres ?? [],
						themes: taxonomy.themes ?? [],
						demographics: taxonomy.demographics ?? [],
						studios: taxonomy.studios ?? [],
						mal_source: taxonomy.mal_source ?? null,
					}
				: {}),
		};
	}
	return null;
}

// Re-point a record at a different source entry. Catalog fields come from
// `fresh` (lock-aware), the external id swaps, everything the user did stays, and
// the previous link is remembered in `relinked_from` — unless `relinkedFrom` is
// passed explicitly (null = forget history, as after an undo). The result is
// un-synced: the cloud row keys on external_id, so db/overrides.js pushes it as
// delete-old + create-new.
export function relinkRecord(record, mediaType, fresh, { taxonomy, relinkedFrom, now = nowIso() } = {}) {
	const key = EXTERNAL_KEY[mediaType];
	if (!key) throw new Error(`Unknown media type ${mediaType}`);
	const patch = catalogPatchFrom(mediaType, fresh, { taxonomy });
	if (!patch || patch[key] == null) throw new Error("Invalid re-link target");
	if (mediaType === "book") {
		// Search results carry no summary/subjects. Clear the old book's so the
		// lazy backfill fetches the right ones on the next open.
		if (patch.summary === undefined) patch.summary = null;
		if (patch.subjects === undefined) patch.subjects = [];
	}
	if (mediaType === "movie") {
		// The re-link sheet fetches the full OMDb record, but if only a search row
		// made it here, never let the old film's plot/people/rating linger.
		for (const k of ["summary", "runtime", "rated", "imdb_rating", "release_date"]) {
			if (patch[k] === undefined) patch[k] = null;
		}
		for (const k of ["genres", "directors", "writers", "cast"]) {
			if (patch[k] === undefined) patch[k] = [];
		}
	}
	const previous = catalogView(record);
	const { next } = applyCatalogFields(record, patch);
	next[key] = patch[key];
	next.relinked_from =
		relinkedFrom === undefined
			? { external_id: String(record[key]), title: previous.title ?? record.title ?? null, at: now }
			: relinkedFrom;
	next.updated_at = now;
	next._synced = false;
	return next;
}
